'use client'

import { useEffect, useState } from 'react'

/**
 * React Hook that that observes the dom for a <form> element given an id. This hook will return the status of the form (pending, invalid, or undefined)
 */
export const useFormListener = (formId: string) => {
  const [formStatus, setFormStatus] = useState<'pending' | 'invalid' | 'valid'>(
    'invalid',
  )
  const [formRef, setFormRef] = useState<HTMLFormElement | undefined>()

  useEffect(() => {
    // The form might already be on the dom during mount
    if (!formRef) {
      setFormRef(
        document?.getElementById(formId) as HTMLFormElement | undefined,
      )
    }
  }, [])

  useEffect(() => {
    // Otherwise, set a mutation observer to watch for the form being added to the dom
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes) {
          mutation.addedNodes.forEach((node) => {
            //@ts-ignore
            if (node.id === formId) {
              setFormRef(node as HTMLFormElement)
            }
          })
        }
      })
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })

    return () => {
      observer.disconnect()
    }
  }, [])

  const updateFormValidity = () => {
    if (formRef) {
      formRef?.checkValidity()
        ? setFormStatus('valid')
        : setFormStatus('invalid')
    }
  }

  const setFormToPending = () => {
    setFormStatus('pending')
  }

  useEffect(() => {
    updateFormValidity()

    if (formRef) {
      formRef.addEventListener('submit', setFormToPending)
      formRef.addEventListener('input', updateFormValidity)
    }
    return () => {
      formRef?.removeEventListener('submit', setFormToPending)
      formRef?.removeEventListener('input', updateFormValidity)
    }
  }, [formRef])

  return { formRef, formStatus }
}
